import { EbiCondition, IEbiField, IEbiFormAttachment, IEbiFormSection } from "modules/ebiForms/typings";

// Bloki wspólne wszystkich wniosków (docs/magistrat/ebi-wnioski: „Załączniki wspólne dla wszystkich wniosków”) — dzięki
// nim każdy formularz zaczyna się tak samo: wnioskodawca z mObywatela, ewentualny mocodawca, sposób odbioru odpowiedzi.

/** Kody pól wnioskodawcy — z nich składamy dane kontaktowe do książki adresowej urzędu (BE) i nagłówek wizualizacji. */
export const APPLICANT = {
  firstName: "imie",
  lastName: "nazwisko",
  pesel: "pesel",
  address: "adres",
  postalCode: "kodPocztowy",
  city: "miejscowosc",
  email: "email",
  phone: "telefon",
} as const;

export const YES_NO = [
  { value: "tak", label: "Tak" },
  { value: "nie", label: "Nie" },
];

const OTHER_ADDRESS: EbiCondition = { field: "korespondencjaInna", equals: true };

/** Wnioskodawca = osoba zalogowana mObywatelem (imię i nazwisko z mObywatela, bez edycji). */
export const applicantSection = (title = "Wnioskodawca"): IEbiFormSection => ({
  code: "wnioskodawca",
  title,
  description:
    "Imię i nazwisko pochodzą z mObywatela. PESEL musi być numerem osoby zalogowanej — urząd otrzyma go razem z wnioskiem.",
  fields: [
    { code: APPLICANT.firstName, type: "text", label: "Imię", prefill: "firstName", readOnly: true, required: true, width: "half" },
    { code: APPLICANT.lastName, type: "text", label: "Nazwisko", prefill: "lastName", readOnly: true, required: true, width: "half" },
    { code: APPLICANT.pesel, type: "pesel", label: "PESEL", required: true, width: "half" },
    { code: APPLICANT.phone, type: "phone", label: "Telefon", width: "half", hint: "Nieobowiązkowo — ułatwia kontakt w sprawie." },
    { code: APPLICANT.address, type: "text", label: "Ulica, numer domu i lokalu", required: true, maxLength: 200 },
    { code: APPLICANT.postalCode, type: "postalCode", label: "Kod pocztowy", required: true, width: "third", placeholder: "00-000" },
    { code: APPLICANT.city, type: "text", label: "Miejscowość", required: true, width: "twoThirds", maxLength: 100 },
    {
      code: APPLICANT.email,
      type: "email",
      label: "Adres e-mail",
      required: true,
      hint: "Na ten adres przyjdzie powiadomienie, gdy urząd odpowie w Elektronicznym Biurze Interesanta.",
    },
    { code: "korespondencjaInna", type: "checkbox", label: "Adres do korespondencji jest inny niż adres zamieszkania" },
    { code: "korAdres", type: "text", label: "Adres do korespondencji — ulica, numer domu i lokalu", required: true, visibleWhen: OTHER_ADDRESS, maxLength: 200 },
    { code: "korKodPocztowy", type: "postalCode", label: "Kod pocztowy", required: true, visibleWhen: OTHER_ADDRESS, width: "third", placeholder: "00-000" },
    { code: "korMiejscowosc", type: "text", label: "Miejscowość", required: true, visibleWhen: OTHER_ADDRESS, width: "twoThirds", maxLength: 100 },
  ],
});

export const PROXY: EbiCondition = { field: "wImieniu", equals: "pelnomocnik" };

/** Pełnomocnik (art. 33 KPA): zalogowany działa w imieniu mocodawcy — mocodawca jest stroną, pełnomocnik dostaje odpowiedź. */
export const representationSection = (): IEbiFormSection => ({
  code: "reprezentacja",
  title: "W czyim imieniu składasz wniosek",
  fields: [
    {
      code: "wImieniu",
      type: "radio",
      label: "Składam wniosek",
      required: true,
      defaultValue: "wlasnym",
      options: [
        { value: "wlasnym", label: "we własnym imieniu" },
        { value: "pelnomocnik", label: "jako pełnomocnik innej osoby, firmy lub instytucji" },
      ],
    },
    { code: "mocodawcaNazwa", type: "text", label: "Imię i nazwisko albo nazwa mocodawcy", required: true, visibleWhen: PROXY, maxLength: 300 },
    { code: "mocodawcaAdres", type: "text", label: "Adres albo siedziba mocodawcy — ulica, numer", required: true, visibleWhen: PROXY, maxLength: 200 },
    { code: "mocodawcaKodPocztowy", type: "postalCode", label: "Kod pocztowy", required: true, visibleWhen: PROXY, width: "third", placeholder: "00-000" },
    { code: "mocodawcaMiejscowosc", type: "text", label: "Miejscowość", required: true, visibleWhen: PROXY, width: "twoThirds", maxLength: 100 },
    {
      code: "pelnomocnictwoZwolnione",
      type: "checkbox",
      label:
        "Jestem małżonkiem, wstępnym, zstępnym lub rodzeństwem mocodawcy — pełnomocnictwo jest zwolnione z opłaty skarbowej",
      visibleWhen: PROXY,
    },
  ],
});

/** Odpowiedź urzędu: domyślnie w EBI (komunikacja dwustronna), alternatywnie tradycyjnie. */
export const replySection = (title = "Sposób odbioru odpowiedzi"): IEbiFormSection => ({
  code: "odbior",
  title,
  fields: [
    {
      code: "sposobOdbioru",
      type: "radio",
      label: "Odpowiedź chcę otrzymać",
      required: true,
      defaultValue: "ebi",
      options: [
        { value: "ebi", label: "w Elektronicznym Biurze Interesanta (powiadomienie e-mailem)" },
        { value: "poczta", label: "pocztą na adres do korespondencji" },
        { value: "osobiscie", label: "osobiście w urzędzie" },
      ],
    },
  ],
});

/** Informacja o opłacie jako pole „info” sekcji. */
export const feeInfo = (code: string, label: string): IEbiField => ({ code, type: "info", label });

/** Pełnomocnictwo + dowód opłaty skarbowej 17 zł (część IV załącznika do ustawy o opłacie skarbowej). */
export const proxyAttachments = (): IEbiFormAttachment[] => [
  {
    code: "pelnomocnictwo",
    label: "Pełnomocnictwo",
    hint: "Skan podpisanego pełnomocnictwa (art. 33 KPA).",
    visibleWhen: PROXY,
    required: true,
  },
  {
    code: "oplataPelnomocnictwo",
    label: "Dowód zapłaty opłaty skarbowej za pełnomocnictwo (17 zł)",
    hint: "Potwierdzenie przelewu na rachunek urzędu.",
    visibleWhen: { all: [PROXY, { not: { field: "pelnomocnictwoZwolnione", equals: true } }] },
    required: true,
  },
];

export const otherAttachments = (): IEbiFormAttachment => ({
  code: "inne",
  label: "Inne załączniki",
  hint: "Dokumenty, które chcesz dołączyć do wniosku (nieobowiązkowo).",
  multiple: true,
});

/** Klauzula pod oświadczeniami składanymi pod rygorem odpowiedzialności karnej (art. 233 § 6 KK — gdy ustawa ją przewiduje). */
export const penaltyDeclaration = (
  code = "klauzula233",
  label = "Jestem świadoma/świadomy odpowiedzialności karnej za złożenie fałszywego oświadczenia.",
): IEbiField => ({ code, type: "declaration", label, required: true });

/** Działki ewidencyjne (obręb + numer), opcjonalnie z numerem księgi wieczystej. */
export const parcelsField = (options: { code?: string; label?: string; withLandRegister?: boolean; max?: number } = {}): IEbiField => ({
  code: options.code ?? "dzialki",
  type: "repeat",
  label: options.label ?? "Działki ewidencyjne",
  itemLabel: "Działka",
  required: true,
  min: 1,
  max: options.max ?? 20,
  fields: [
    { code: "obreb", type: "text", label: "Obręb ewidencyjny (nazwa lub numer)", required: true, width: "half", maxLength: 100 },
    { code: "numer", type: "text", label: "Numer działki", required: true, width: "half", maxLength: 50, placeholder: "np. 125/4" },
    ...(options.withLandRegister
      ? [{ code: "kw", type: "text", label: "Numer księgi wieczystej (jeśli założona)", maxLength: 30, placeholder: "np. LU1I/00012345/6" } satisfies IEbiField]
      : []),
  ],
});

/** Dowód zapłaty opłaty skarbowej lub administracyjnej — potwierdzenie przelewu na rachunek urzędu. */
export const feeProofAttachment = (
  label = "Dowód zapłaty opłaty skarbowej",
  options: Partial<IEbiFormAttachment> = {},
): IEbiFormAttachment => ({
  code: "dowodOplaty",
  label,
  hint: "Potwierdzenie przelewu na rachunek urzędu (PDF lub zdjęcie).",
  required: true,
  ...options,
});

/** Pomoc de minimis (przedsiębiorca, rolnik): formularz informacji i zaświadczenia z ostatnich 3 lat albo oświadczenie. */
export const deMinimisAttachments = (visibleWhen?: EbiCondition): IEbiFormAttachment[] => [
  {
    code: "deMinimisFormularz",
    label: "Formularz informacji przedstawianych przy ubieganiu się o pomoc de minimis",
    hint: "Aktualny wzór z rozporządzenia Rady Ministrów (formularz udostępnia urząd albo UOKiK).",
    required: true,
    visibleWhen,
  },
  {
    code: "deMinimisZaswiadczenia",
    label: "Zaświadczenia o pomocy de minimis otrzymanej w ciągu ostatnich 3 lat albo oświadczenie o jej nieotrzymaniu",
    required: true,
    visibleWhen,
  },
];

/** Przedsiębiorca, w imieniu którego działa zalogowany (właściciel firmy z CEIDG albo osoba reprezentująca według KRS). */
export const businessSection = (title = "Przedsiębiorca", visibleWhen?: EbiCondition): IEbiFormSection => ({
  code: "przedsiebiorca",
  title,
  visibleWhen,
  fields: [
    { code: "firmaNazwa", type: "text", label: "Firma (nazwa) przedsiębiorcy", required: true, maxLength: 300 },
    { code: "firmaNip", type: "nip", label: "NIP", required: true, width: "half" },
    { code: "firmaRegon", type: "text", label: "REGON", width: "half", maxLength: 14 },
    { code: "firmaKrs", type: "text", label: "Numer KRS (jeśli przedsiębiorca jest wpisany do KRS)", width: "half", maxLength: 10 },
    { code: "firmaReprezentacja", type: "text", label: "Podstawa reprezentacji (np. właściciel, prezes zarządu)", width: "half", maxLength: 200 },
    { code: "firmaAdres", type: "text", label: "Adres siedziby — ulica, numer", required: true, maxLength: 200 },
    { code: "firmaKodPocztowy", type: "postalCode", label: "Kod pocztowy", required: true, width: "third", placeholder: "00-000" },
    { code: "firmaMiejscowosc", type: "text", label: "Miejscowość", required: true, width: "twoThirds", maxLength: 100 },
  ],
});
